import "./ManageUsersPage.css";
import "../../modals/Modal.css";
import { useCallback, useContext, useEffect, useState } from "react";
import { UsersContext } from "../../store/UsersContext";
import { ModalContext } from "../../store/ModalContext";
import Searchbar from "../../components/Searchbar";
import { UserListItem } from "../../../../api/auth";
import { formatPhoneNumber } from "../../utils/PhoneFormatters";
import { CreateUserModalBody } from "./modals/CreateUserModalBody";
import ScrollableContainer from "../../components/ScrollableContainer";
import EditCsaSubscriptionOnClient from "./EditCsaSubscriptionOnClient";
import { PickupContext } from "../../store/PickupContext";
import { useAuthUser } from "../../hooks/useAuthUser";
import LoadingSpinner from "../../../LoadingSpinner";

const ManageUsersPage = () => {
  const { userId: currentUserId } = useAuthUser();
  const { users, loading, refetch, promptDeleteUser } =
    useContext(UsersContext);
  const { openModal } = useContext(ModalContext);
  const { pickups } = useContext(PickupContext);
  const [filteredAccounts, setFilteredAccounts] = useState<UserListItem[]>([]);
  const [filteredText, setFilteredText] = useState("");

  const usersToDisplay = filteredText ? filteredAccounts : users;
  const tableRows = usersToDisplay.map((user) => {
    const userPickup = pickups.find((pickup) => pickup.userId === user.userId);
    const hasCsaSubscription = user.hasCSASubscription;
    const paymentStatus = userPickup?.subscriptionPaymentStatus;
    const hasNotPaid = paymentStatus !== "paid";
    return (
      <tr key={user.userId}>
        <td className="td capitalized">
          {hasCsaSubscription && hasNotPaid ? (
            <span className="unpaid-tag">
              {user.firstName} {user.secondName} {user.surname}
            </span>
          ) : (
            user.firstName + " " + user.surname
          )}
        </td>
        <td className="td capitalized">{user.role}</td>
        <td className="td">{user.email}</td>
        <td className="td">{formatPhoneNumber(user.phoneNumber)}</td>
        <td className="td td-centered">
          {user.role !== "business" ? (
            <button className="btn" onClick={() => handleEditUser(user)}>
              Edit
            </button>
          ) : (
            ""
          )}
        </td>
        <td className="td">
          {user.role !== "business" ? (
            <button
              className="btn"
              onClick={() => handleViewSubscriptionHistory(user.userId)}
            >
              view
            </button>
          ) : (
            ""
          )}
        </td>
        <td className="td">
          {user.userId === currentUserId ? (
            "—"
          ) : (
            <button
              className="btn btn-red"
              onClick={() => promptDeleteUser(user.userId)}
            >
              Delete
            </button>
          )}
        </td>
      </tr>
    );
  });
  const filterAccounts = useCallback(
    (value: string) => {
      const filteredUsers = users.filter(
        (user) =>
          user.firstName.toLowerCase().includes(value.toLowerCase()) ||
          user.email.toLowerCase().includes(value.toLowerCase()) ||
          user.surname.toLowerCase().includes(value.toLowerCase()) ||
          user.role?.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredAccounts(filteredUsers);
      setFilteredText(value);
    },
    [users],
  );
  function handleEditUser(user: UserListItem) {
    openModal({
      showCloseButton: true,
      body: <EditCsaSubscriptionOnClient user={user} />,
    });
  }
  function handleViewSubscriptionHistory(userId: string) {
    const user = users.find((user) => user.userId === userId);
    const userSubscriptionHistory = user?.subscriptionHistory?.map(
      (subscription) => (
        <div className="subscription-history-container">
          <p key={subscription.year}>{subscription.year}</p>
          <p>{subscription.subscriptionTierName}</p>
        </div>
      ),
    );
    const userSubscriptionHistoryDisplay =
      userSubscriptionHistory && userSubscriptionHistory.length > 0 ? (
        userSubscriptionHistory
      ) : (
        <p>No subscription history found on this user.</p>
      );

    openModal({
      title: `Subscription History 
    ${user?.firstName} ${user?.surname}`,
      body: userSubscriptionHistoryDisplay,
      showCloseButton: true,
    });
  }

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!filteredText) return;
    filterAccounts(filteredText);
    // Recompute active filtered view when users context changes.
  }, [users, filteredText, filterAccounts]);

  return (
    <div className="page-container">
      <h1>User Management</h1>
      <div className="btn-aligned-right-box">
        <button
          className="btn btn-aligned-right"
          onClick={() =>
            openModal({
              showCloseButton: true,
              body: <CreateUserModalBody />,
            })
          }
        >
          Create New User
        </button>
      </div>
      <div className="page-content user-management-page-content">
        <div>
          <Searchbar
            action={filterAccounts}
            placeholder={"Search for account..."}
          />
        </div>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="users-table-container">
            <ScrollableContainer>
              <table className="table">
                <thead className="sticky-table-head">
                  <tr>
                    <th className="th">Name</th>
                    <th className="th">Role</th>
                    <th className="th">Email</th>
                    <th className="th">Phone</th>
                    <th className="th">Subscription</th>
                    <th className="th">Subscription History</th>
                    <th className="th">Delete Account</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredText && filteredAccounts.length === 0 ? (
                    <p className="not-found-message">No users were found.</p>
                  ) : (
                    tableRows
                  )}
                </tbody>
              </table>
            </ScrollableContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageUsersPage;
